import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { resizeImageForUpload } from "@/lib/imageResize";
import { Camera, UploadSimple, CheckCircle, SpinnerGap, Planet, Confetti, SignIn, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function EventUpload() {
  const { code } = useParams();
  const { user } = useAuth();
  const cameraRef = useRef();
  const galleryRef = useRef();
  const [album, setAlbum] = useState(null);   // null=loading, false=not found
  const [name, setName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(0);
  const [busyCount, setBusyCount] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [gallery, setGallery] = useState(null);

  useEffect(() => {
    api.get(`/public/events/${code}`).then((r) => setAlbum(r.data)).catch(() => setAlbum(false));
  }, [code]);

  useEffect(() => {
    if (user && user.name && !name) setName(user.name);
  }, [user]); // eslint-disable-line

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(
      (f) => f.type?.startsWith("image/") || /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(f.name)
    );
    if (!files.length) return;
    setUploading(true);
    setBusyCount(files.length);
    let ok = 0;
    for (const f of files) {
      let toSend = f;
      try { toSend = await resizeImageForUpload(f); } catch { /* original */ }
      const fd = new FormData();
      fd.append("file", toSend);
      if (name.trim()) fd.append("contributor_name", name.trim());
      try {
        await api.post(`/public/events/${code}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
        setDone((d) => d + 1);
      } catch (e) {
        if (e.response?.status === 401) { toast.error("Please sign in to add photos to this album"); break; }
        toast.error(`${f.name}: upload failed`);
      }
    }
    setBusyCount(0);
    setUploading(false);
    if (ok) toast.success(`Thanks! ${ok} photo${ok === 1 ? "" : "s"} added to ${album?.name}`);
  };

  const openGallery = async () => {
    setShowGallery(true);
    if (!gallery) {
      try { const r = await api.get(`/public/events/${code}/gallery`); setGallery(r.data.photos || []); }
      catch { setGallery([]); }
    }
  };

  if (album === null) {
    return <div className="grid min-h-screen place-items-center bg-[#0b1020]"><SpinnerGap size={34} className="animate-spin text-white" weight="bold" /></div>;
  }
  if (album === false) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#0b1020] px-6 text-center text-white">
        <div>
          <Planet size={40} weight="fill" className="mx-auto text-white/70" />
          <p className="mt-4 font-display text-xl font-black">Album not found</p>
          <p className="mt-1 text-sm text-white/70">This event link may have expired.</p>
          <Link to="/" className="mt-6 inline-block rounded-full bg-white px-5 py-2 text-sm font-bold text-[#0b1020]">Go to PhotoPulse</Link>
        </div>
      </div>
    );
  }

  const needsLogin = album.require_login && !user;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0b1020] to-[#132043] text-white">
      <div className="mx-auto max-w-md px-5 py-10">
        <div className="flex items-center gap-2 text-white/80">
          <Planet size={22} weight="fill" className="text-white" />
          <span className="font-display text-sm font-black tracking-tight">PHOTOPULSE<span className="text-white/60">.pro</span></span>
        </div>

        <p className="mt-8 overline text-[11px] text-white/60">Event album</p>
        <h1 className="mt-1 font-display text-3xl font-black leading-tight tracking-tight">{album.name}</h1>
        <p className="mt-2 text-sm text-white/70">
          Add your photos from this event. They go to the organiser first — the best get published to the public gallery.
        </p>

        {needsLogin ? (
          <div className="mt-8 rounded-2xl bg-white/10 p-6 text-center backdrop-blur">
            <SignIn size={30} weight="bold" className="mx-auto text-white" />
            <p className="mt-3 font-display text-lg font-bold">Sign in to contribute</p>
            <p className="mt-1 text-sm text-white/70">The organiser asked contributors to sign in first.</p>
            <Link to={`/login?next=${encodeURIComponent(`/a/${code}`)}`}
              data-testid="event-signin"
              className="mt-5 inline-block rounded-full bg-white px-6 py-2.5 text-sm font-bold text-[#0b1020]">
              Sign in
            </Link>
          </div>
        ) : (
          <>
            <label className="mt-8 block text-sm font-semibold text-white/80">Your name <span className="font-normal text-white/50">(shown as the photo credit — optional)</span></label>
            <input
              data-testid="event-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Alex Rivera"
              className="mt-1.5 w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-base text-white placeholder-white/40 outline-none focus:border-white/40"
            />

            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
              data-testid="event-camera-input" onChange={(e) => handleFiles(e.target.files)} />
            <input ref={galleryRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden"
              data-testid="event-gallery-input" onChange={(e) => handleFiles(e.target.files)} />

            <div className="mt-6 grid grid-cols-1 gap-3">
              <button
                data-testid="event-take-photo"
                onClick={() => cameraRef.current?.click()}
                disabled={uploading}
                className="flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-5 text-lg font-black text-[#0b1020] shadow-lg transition-transform active:scale-[0.98] disabled:opacity-60">
                <Camera size={24} weight="fill" /> Take a photo
              </button>
              <button
                data-testid="event-choose"
                onClick={() => galleryRef.current?.click()}
                disabled={uploading}
                className="flex items-center justify-center gap-2 rounded-2xl border border-white/25 bg-white/5 px-6 py-4 text-base font-bold text-white backdrop-blur transition-colors hover:bg-white/10 disabled:opacity-60">
                <UploadSimple size={20} weight="bold" /> Choose from gallery
              </button>
            </div>

            {uploading && (
              <div className="mt-5 flex items-center gap-2 text-sm text-white/80">
                <SpinnerGap size={18} className="animate-spin" weight="bold" /> Uploading {busyCount} photo{busyCount === 1 ? "" : "s"}…
              </div>
            )}

            {done > 0 && !uploading && (
              <div data-testid="event-success" className="mt-6 flex items-center gap-2 rounded-2xl bg-emerald-500/15 px-4 py-3 text-emerald-200">
                <Confetti size={22} weight="fill" />
                <span className="text-sm font-semibold">{done} photo{done === 1 ? "" : "s"} added — thank you! Add more anytime.</span>
              </div>
            )}

            <p className="mt-8 flex items-center gap-1 text-xs text-white/50">
              <CheckCircle size={13} weight="fill" /> Photos are compressed on your phone for a fast, data-light upload.
            </p>

            {album.public_gallery && (
              <button data-testid="event-see-all" onClick={openGallery}
                className="mt-6 w-full rounded-2xl border border-white/20 bg-white/5 py-3 text-sm font-bold text-white backdrop-blur transition-colors hover:bg-white/10">
                See everyone&apos;s photos
              </button>
            )}
          </>
        )}
      </div>

      {showGallery && (
        <div className="fixed inset-0 z-50 flex flex-col bg-[#0b1020]" data-testid="event-guest-gallery">
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
            <div>
              <p className="overline text-[10px] text-white/50">Everyone&apos;s photos</p>
              <h2 className="font-display text-lg font-black text-white">{album.name}</h2>
            </div>
            <button onClick={() => setShowGallery(false)} className="grid h-9 w-9 place-items-center rounded-md text-white hover:bg-white/10"><X size={20} weight="bold" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {gallery === null ? (
              <div className="grid place-items-center py-20"><SpinnerGap size={30} className="animate-spin text-white" weight="bold" /></div>
            ) : gallery.length === 0 ? (
              <p className="py-20 text-center text-sm text-white/60">No photos yet — be the first to add one!</p>
            ) : (
              <div className="columns-2 gap-3 [&>*]:mb-3">
                {gallery.map((p) => (
                  <div key={p.id} className="break-inside-avoid overflow-hidden rounded-lg bg-white/5">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${p.image_url}`} alt={p.title || "photo"} loading="lazy" className="w-full" />
                    {(p.title || p.photographer) && (
                      <div className="p-2">
                        {p.title && <p className="line-clamp-1 text-xs font-semibold text-white">{p.title}</p>}
                        {p.photographer && <p className="text-[10px] text-white/60">by {p.photographer}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
